<script setup>
// Auto-extracted chrome for Heizenmitwaermepumpe.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "Heizen mit Wärmepumpe" },
    brandTagline: { type: String, default: "Wissen, das vor dem Kauf hilft." },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "Heizen mit Wärmepumpe");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Wissen, das vor dem Kauf hilft.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&display=swap";
</script>

<template>
    <div class="hmw-root" :style="`--hmw-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="hmw-topbar">
                    <div class="hmw-topbar__inner">
                        <a href="/" class="hmw-brand">
                            <span class="hmw-brand__mark" aria-hidden="true">HW</span>
                            <span class="hmw-brand__text">
                                <span class="hmw-brand__name">{{ brandName }}</span>
                                <span class="hmw-brand__tag">{{ brandTagline }}</span>
                            </span>
                        </a>
                        <nav class="hmw-nav" aria-label="Wiki-Navigation">
                            <a href="#hmw-stubs">Wissen</a>
                            <a href="#hmw-grundlagen">Grundlagen</a>
                            <a href="#hmw-faq">FAQ</a>
                            <a href="#hmw-check" class="hmw-nav__cta">WP-Check starten</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <footer class="hmw-footer">
                        <p>
                            {{ brandName }} - {{ brandTagline }} - Ein redaktioneller Wiki-Hub. Keine Verkaufsseite, keine
                            Affiliate-Verlinkung. Alle Rechenwege offen dokumentiert.
                        </p>
                        <nav>
                            <a href="/impressum">Impressum</a>
                            <a href="/datenschutz">Datenschutz</a>
                            <a href="/agb">AGB</a>
                            <a href="/widerruf">Widerruf</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </footer>
    </div>
</template>

<style scoped>

.hmw-root {
    --hmw-bg: #fbfaf7;
    --hmw-paper: #ffffff;
    --hmw-ink: #0b1220;
    --hmw-muted: #4b5563;
    --hmw-rule: #e5e7eb;
    --hmw-accent-soft: rgba(30, 64, 175, 0.08);
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: var(--hmw-ink);
    background: var(--hmw-bg);
    line-height: 1.6;
}
.hmw-root *,
.hmw-root *::before,
.hmw-root *::after {
    box-sizing: border-box;
}
.hmw-root h1,
.hmw-root h2,
.hmw-root h3 {
    font-family: 'Space Grotesk', system-ui, sans-serif;
    letter-spacing: -0.01em;
    color: var(--hmw-ink);
    margin: 0;
}
.hmw-topbar {
    border-bottom: 1px solid var(--hmw-rule);
    background: var(--hmw-paper);
    position: sticky;
    top: 0;
    z-index: 20;
}
.hmw-topbar__inner {
    max-width: 1180px;
    margin: 0 auto;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
}
.hmw-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: inherit;
}
.hmw-brand__mark {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    background: var(--hmw-accent);
    color: #fff;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    letter-spacing: 0.02em;
}
.hmw-brand__text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
}
.hmw-brand__name {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 16px;
}
.hmw-brand__tag {
    font-size: 12px;
    color: var(--hmw-muted);
}
.hmw-nav {
    display: flex;
    align-items: center;
    gap: 22px;
    font-size: 14px;
}
.hmw-nav a {
    color: var(--hmw-muted);
    text-decoration: none;
    font-weight: 500;
}
.hmw-nav a:hover {
    color: var(--hmw-ink);
}
.hmw-nav__cta {
    color: var(--hmw-accent) !important;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 12.5px;
    border: 1px solid var(--hmw-accent);
    padding: 8px 14px;
    border-radius: 6px;
}
@media (max-width: 720px) {
    .hmw-nav a:not(.hmw-nav__cta) {
        display: none;
    }
}

.hmw-main {
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 20px;
}
.hmw-hero {
    padding: 56px 0 40px;
}
.hmw-hero__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
    gap: 40px;
    align-items: start;
}
@media (max-width: 960px) {
    .hmw-hero__grid {
        grid-template-columns: 1fr;
    }
}
.hmw-hero__kicker {
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 11.5px;
    color: var(--hmw-accent);
    margin: 0 0 16px;
    font-weight: 600;
}
.hmw-hero__title {
    font-size: clamp(36px, 5vw, 56px);
    line-height: 1.05;
    font-weight: 700;
    margin-bottom: 18px;
}
.hmw-hero__lede {
    font-size: 18px;
    color: var(--hmw-muted);
    max-width: 56ch;
    margin: 0 0 24px;
}
.hmw-hero__chips {
    list-style: none;
    padding: 0;
    margin: 0 0 28px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.hmw-hero__chips li {
    background: var(--hmw-paper);
    border: 1px solid var(--hmw-rule);
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12.5px;
    color: var(--hmw-muted);
}
.hmw-hero__ctaRow {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
}
.hmw-cta {
    background: var(--hmw-accent);
    color: #fff;
    border: 0;
    padding: 16px 28px;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 13.5px;
    cursor: pointer;
    border-radius: 2px;
    transition: transform 120ms ease, background 120ms ease;
}
.hmw-cta:hover {
    background: #1e3a8a;
}
.hmw-cta:disabled {
    background: #94a3b8;
    cursor: not-allowed;
}
.hmw-cta--small {
    padding: 12px 20px;
    font-size: 12.5px;
}
.hmw-link {
    color: var(--hmw-muted);
    text-decoration: underline;
    font-size: 14px;
}
.hmw-hero__byline {
    margin-top: 24px;
    font-size: 13px;
    color: var(--hmw-muted);
    padding-top: 16px;
    border-top: 1px solid var(--hmw-rule);
}

.hmw-sidecard {
    background: var(--hmw-paper);
    border: 1px solid var(--hmw-rule);
    border-radius: 4px;
    box-shadow: 0 1px 0 rgba(11, 18, 32, 0.04);
    overflow: hidden;
    position: sticky;
    top: 86px;
}
@media (max-width: 960px) {
    .hmw-sidecard {
        position: static;
    }
}
.hmw-sidecard__head {
    padding: 22px 22px 14px;
    border-bottom: 1px solid var(--hmw-rule);
    background: var(--hmw-accent-soft);
}
.hmw-sidecard__kicker {
    margin: 0 0 4px;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11px;
    color: var(--hmw-accent);
    font-weight: 600;
}
.hmw-sidecard__title {
    font-size: 22px;
    margin-bottom: 14px;
}
.hmw-progress {
    height: 4px;
    background: rgba(30, 64, 175, 0.15);
    border-radius: 2px;
    overflow: hidden;
}
.hmw-progress__bar {
    display: block;
    height: 100%;
    background: var(--hmw-accent);
    transition: width 220ms ease;
}
.hmw-progress__label {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--hmw-muted);
}
.hmw-sidecard__body {
    padding: 22px;
}
.hmw-step h3 {
    font-size: 17px;
    margin-bottom: 14px;
}
.hmw-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
    font-size: 14px;
    color: var(--hmw-muted);
}
.hmw-field--inline {
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
}
.hmw-field input[type='text'],
.hmw-field input[type='email'],
.hmw-field input[type='tel'],
.hmw-field input[type='number'],
.hmw-field select {
    font: inherit;
    font-size: 16px;
    padding: 11px 12px;
    border: 1px solid var(--hmw-rule);
    border-radius: 4px;
    background: #fff;
    color: var(--hmw-ink);
}
.hmw-field input:focus,
.hmw-field select:focus {
    outline: 2px solid var(--hmw-accent);
    outline-offset: 1px;
}
.hmw-row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
}
.hmw-cards {
    border: 0;
    padding: 0;
    margin: 0 0 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.hmw-cards legend {
    font-size: 14px;
    color: var(--hmw-muted);
    margin-bottom: 8px;
    padding: 0;
}
.hmw-cards label {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid var(--hmw-rule);
    padding: 11px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    color: var(--hmw-ink);
    transition: border-color 120ms ease, background 120ms ease;
    min-height: 44px;
}
.hmw-cards label:has(input:checked) {
    border-color: var(--hmw-accent);
    background: var(--hmw-accent-soft);
}
.hmw-cards input[type='radio'] {
    accent-color: var(--hmw-accent);
}
.hmw-hint {
    font-size: 12.5px;
    color: var(--hmw-muted);
    margin: 0 0 8px;
}
.hmw-consent {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 12px;
    color: var(--hmw-muted);
    line-height: 1.5;
    margin: 12px 0 4px;
}
.hmw-consent input {
    margin-top: 3px;
    accent-color: var(--hmw-accent);
}
.hmw-err {
    color: #b91c1c;
    font-size: 13px;
    margin: 8px 0 0;
}
.hmw-stepnav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 18px;
    gap: 10px;
}
.hmw-back {
    background: transparent;
    border: 0;
    color: var(--hmw-muted);
    font: inherit;
    font-size: 13.5px;
    cursor: pointer;
    text-decoration: underline;
    padding: 8px 0;
}
.hmw-preview {
    background: #f1f5f9;
    border: 1px solid var(--hmw-rule);
    border-radius: 4px;
    padding: 12px 14px;
    margin: 8px 0 12px;
}
.hmw-preview__kicker {
    margin: 0 0 6px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--hmw-muted);
    font-weight: 600;
}
.hmw-preview dl {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}
.hmw-preview dt {
    font-size: 11px;
    color: var(--hmw-muted);
}
.hmw-preview dd {
    margin: 0;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 16px;
}

.hmw-result {
    padding: 22px;
}
.hmw-result__badge {
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 11.5px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 2px;
    background: var(--hmw-accent-soft);
    color: var(--hmw-accent);
    margin-bottom: 12px;
}
.hmw-result__badge[data-state='passt'] {
    background: #dcfce7;
    color: #166534;
}
.hmw-result__badge[data-state='noch_nicht'] {
    background: #fee2e2;
    color: #991b1b;
}
.hmw-result__big {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.1;
    margin: 0 0 16px;
}
.hmw-kpis {
    margin: 0 0 14px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}
.hmw-kpis dt {
    font-size: 11px;
    color: var(--hmw-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
}
.hmw-kpis dd {
    margin: 2px 0 0;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 600;
    font-size: 18px;
}
.hmw-reasons {
    list-style: disc;
    padding-left: 18px;
    margin: 0 0 14px;
    color: var(--hmw-muted);
    font-size: 13.5px;
}
.hmw-reasons li + li {
    margin-top: 4px;
}
.hmw-result__next {
    font-size: 13px;
    color: var(--hmw-muted);
}

.hmw-stubs {
    padding: 56px 0;
    border-top: 1px solid var(--hmw-rule);
}
.hmw-stubs__head {
    margin-bottom: 28px;
}
.hmw-eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--hmw-accent);
    margin: 0 0 10px;
}
.hmw-stubs__head h2 {
    font-size: clamp(26px, 3vw, 36px);
    max-width: 28ch;
}
.hmw-stubs__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 18px;
}
@media (max-width: 880px) {
    .hmw-stubs__grid {
        grid-template-columns: 1fr;
    }
}
.hmw-stub {
    background: var(--hmw-paper);
    border: 1px solid var(--hmw-rule);
    border-left: 3px solid var(--hmw-accent);
    padding: 22px 22px 20px;
    border-radius: 2px;
    display: flex;
    flex-direction: column;
}
.hmw-stub__kicker {
    margin: 0 0 6px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 11px;
    color: var(--hmw-accent);
    font-weight: 600;
}
.hmw-stub__title {
    font-size: 21px;
    margin-bottom: 10px;
}
.hmw-stub__teaser {
    color: var(--hmw-muted);
    font-size: 14.5px;
    margin: 0 0 14px;
}
.hmw-stub__list {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    font-size: 13px;
}
.hmw-stub__list li {
    padding: 6px 0;
    border-top: 1px dashed var(--hmw-rule);
    color: var(--hmw-ink);
}
.hmw-stub__list li:first-child {
    border-top: 0;
}
.hmw-stub__more {
    margin-top: auto;
    color: var(--hmw-accent);
    text-decoration: none;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.1em;
}

.hmw-prose {
    padding: 56px 0;
    border-top: 1px solid var(--hmw-rule);
    max-width: 72ch;
}
.hmw-prose h2 {
    font-size: clamp(24px, 2.6vw, 32px);
    margin-bottom: 18px;
}
.hmw-prose p {
    color: var(--hmw-muted);
    margin: 0 0 14px;
    font-size: 16px;
}

.hmw-faq {
    padding: 56px 0 80px;
    border-top: 1px solid var(--hmw-rule);
}
.hmw-faq__item {
    border-top: 1px solid var(--hmw-rule);
    padding: 14px 0;
}
.hmw-faq__item:last-of-type {
    border-bottom: 1px solid var(--hmw-rule);
}
.hmw-faq__item summary {
    cursor: pointer;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 17px;
    font-weight: 600;
    padding: 6px 0;
}
.hmw-faq__item p {
    color: var(--hmw-muted);
    margin: 8px 0 0;
}

.hmw-footer {
    border-top: 1px solid var(--hmw-rule);
    padding: 28px 0 64px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 13px;
    color: var(--hmw-muted);
}
.hmw-footer nav {
    display: flex;
    gap: 18px;
}
.hmw-footer a {
    color: var(--hmw-muted);
    text-decoration: none;
}
.hmw-footer a:hover {
    color: var(--hmw-ink);
}

.hmw-sticky {
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
    background: var(--hmw-accent);
    color: #fff;
    text-align: center;
    padding: 14px 16px;
    font-family: 'Space Grotesk', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 13px;
    font-weight: 600;
    border-radius: 2px;
    text-decoration: none;
    box-shadow: 0 10px 26px rgba(11, 18, 32, 0.22);
    z-index: 30;
}
@media (min-width: 960px) {
    .hmw-sticky {
        display: none;
    }
}
@media (prefers-reduced-motion: reduce) {
    .hmw-progress__bar,
    .hmw-cta {
        transition: none;
    }
}

/* ── Trust strip (images + cites + byline) — hmw-* unique treatment ── */
.hmw-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.hmw-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .hmw-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.hmw-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 14px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid rgba(20,17,13,0.10); border-right: 6px solid #1E40AF; box-shadow: 0 6px 22px -14px rgba(20,17,13,0.20);
}
.hmw-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.hmw-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.hmw-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.hmw-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.hmw-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.hmw-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #1E40AF55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.hmw-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.hmw-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #1E40AF;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.hmw-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.hmw-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.hmw-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.hmw-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.hmw-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.hmw-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


</style>
